import { Vector3 } from 'three/webgpu';
import { CIRCLE_CLEAR } from './resolve';
import type { Weapon } from './weapons';

/** What the attack needs to know about the player each physics step (universe space). */
export interface Frame {
  /** Feet, and the eye the weapon swings from. */
  feet: Vector3;
  eye: Vector3;
  /** Where the player looks (unit), and "up" against the local gravity (unit). */
  forward: Vector3;
  up: Vector3;
  grounded: boolean;
}

/**
 * How the player moved during an attack, as it lands: what its three bonuses come from.
 *
 * All three are measured over the strike (from the release to the hit), so a heavy weapon's
 * longer strike has time to build more of them. Rotation and finesse trade off: the view's
 * turning is capped while attacking (strikeSwing over the strike). Rotation is how far the aim
 * ends up from where it was at the release, finesse how far it went and came back. Angles are
 * measured side to side and up and down, with up and down counting PITCH_WEIGHT times (the
 * view only spans π of pitch).
 */
export interface Motion {
  /** 0–1: how much of the weapon's charge time the button was held. */
  charge: number;
  hold: number;
  /**
   * Rotation: radians between where the aim was at the release and where it is at the hit
   * (net: going out and back again cancels).
   */
  arc: number;
  /**
   * Finesse: from the finesse angle (see Attack.finesseAngle), the farthest the aim got during
   * the strike, times how little of all its turning went anywhere: farthest × (1 − net / total).
   * High when the aim goes out and comes back (a feint), 0 for one smooth swing.
   */
  feint: number;
  /** The finesse angle's direction from the eye (unit, universe), and the farthest the aim got from it (radians). */
  finesseDir: Vector3;
  feintFar: number;
  /** There's a finesse circle to crit on (see Attack.circle). */
  circle: boolean;
  /**
   * The weapon tip's velocity from the swing (m/s, universe): its fastest SWING_WINDOW during
   * the strike (from just before the release to the hit). Knockback follows it.
   */
  swing: Vector3;
  /** Radians the strike itself turned (signed): the swing sweeps across what it turned past. */
  sweep: number;
  /** The view's directions during the strike, from the release to the hit: the weapon's path. */
  path: Vector3[];
  /** Movement: how far the player moved from the release to the hit (m, universe). */
  travel: Vector3;
  /** The feet and eye at the hit, and the view then. */
  feet: Vector3;
  eye: Vector3;
  forward: Vector3;
  up: Vector3;
}

export type Phase = 'idle' | 'windup' | 'strike' | 'recover';

/** Seconds the swing's speed is measured over. */
const SWING_WINDOW = 0.15;
/** The strike's swing is looked for from this long before the release (s), and at most this far back. */
const SWING_LEAD = 0.1;
const SWING_SEARCH = 0.8;
/** Up and down counts this much more than side to side (pitch spans only π). */
export const PITCH_WEIGHT = 1.5;
/**
 * The finesse angle is placed where the aim would get to if it turned back the other way at this
 * share of the speed it was turning, for the weapon's strike delay. The view turns at most at
 * the weapon's swing speed while attacking (swingLookRate, windup included), so this is at most
 * half the weapon's strike swing.
 */
const FINESSE_LEAD = 0.5;
/**
 * The swing it's measured from is the moment before the release: the way the aim was turning
 * RELEASE_LOOKBACK s before it (within the windup), at the fastest it turned that way since,
 * from the farthest point it reached that way (where it turned back, if it did). Players switch direction as they let go, and sometimes a moment
 * before: the circle goes where it would have had they let go as they turned.
 */
const RELEASE_LOOKBACK = 0.05;
/** The swing's direction is taken as the lookback began, unless the aim was turning slower than this share of its fastest in it then. */
const SWING_START = 0.25;
/** Its speed is its average over up to this long (s) leading to where it turned back. */
const SPEED_WINDOW = 0.2;
/** The aim's turn rate is smoothed over this long (s). */
const AIM_SMOOTH = 0.03;
/** Steps kept (3 s at 60 Hz): the longest attack a rotation can be measured over. */
const STEPS = 180;

const _a = new Vector3();
const _b = new Vector3();
const _c = new Vector3();
const _then = new Vector3();
const _tipV = new Vector3();

/**
 * One weapon's attack: idle → windup (button held) → strike (released; the hit comes after
 * the weapon's strike delay) → recover. An attack made in the air is no different: coming
 * down on an enemy counts as movement toward it. `update` returns the motion on the step the
 * hit lands, else null.
 */
export class Attack {
  phase: Phase = 'idle';
  /** Seconds in the current phase, and how long the button was held. */
  time = 0;
  hold = 0;
  /**
   * Set at the release, for the strike: the finesse angle's direction from the eye (universe),
   * for showing it. Finesse is measured from it, not from the aim at the release: turning as you
   * release puts it behind the swing, so turning back across fakes the enemy out.
   */
  readonly finesseAngle = new Vector3();
  /**
   * Set at the release: whether there's a finesse circle this strike. There isn't when the aim
   * is already within CIRCLE_CLEAR of the finesse angle (released still, or turning too slowly):
   * a critical should take a feint.
   */
  circle = false;
  private readonly lastForward = new Vector3();
  private lastPitch = 0;
  private hasLast = false;
  /** Per step: turn about up, where the aim points, the view; rings over the last STEPS steps. */
  private readonly turns = new Float32Array(STEPS);
  private readonly yaws = new Float64Array(STEPS);
  private readonly pitches = new Float32Array(STEPS);
  private readonly views = new Float32Array(STEPS * 3);
  /** Per step: the aim's smoothed turn rate (yaw, pitch; rad/s). */
  private readonly rateYaw = new Float32Array(STEPS);
  private readonly ratePitch = new Float32Array(STEPS);
  private readonly dts = new Float32Array(STEPS);
  private head = 0;
  /** The aim's smoothed angular velocity (yaw, pitch rad/s), and where it points (yaw summed, pitch). */
  private wYaw = 0;
  private wPitch = 0;
  private aimYaw = 0;
  private aimPitch = 0;
  /** Where the feet were at the release and are now (for movement). */
  private readonly releaseFeet = new Vector3();
  private readonly lastFeet = new Vector3();
  /** Where the aim was at the release (for rotation). */
  private startYaw = 0;
  private startPitch = 0;
  /**
   * Finesse, in the strike: the finesse angle (yaw, weighted pitch), all the aim's turning since
   * it (from it to the release, then the strike's path), the farthest the aim got from it, and
   * where the aim was last step.
   */
  private feintYaw = 0;
  private feintPitch = 0;
  private feintTotal = 0;
  private feintFar = 0;
  private lastYaw = 0;
  private lastWeighted = 0;
  private releasedAgo = 0;
  /** Seconds since the button went down (0 when idle). */
  private attackTime = 0;

  constructor(readonly weapon: Weapon) {}

  get charge(): number {
    return Math.min(1, this.hold / this.weapon.chargeTime);
  }

  private get active(): boolean {
    return this.phase === 'windup' || this.phase === 'strike';
  }

  /** The bonuses' measures so far in this attack, for the HUD. */
  get arc(): number {
    return this.phase === 'strike' ? this.netArc() : 0;
  }

  get feint(): number {
    return this.phase === 'strike' ? this.feintSoFar() : 0;
  }

  swingVelocity(out: Vector3): Vector3 {
    return this.active ? this.tipVelocity(0, Math.min(SWING_WINDOW, this.attackTime), out) : out.set(0, 0, 0);
  }

  /** How far the feet have moved since the release, in the strike. */
  travel(out: Vector3): Vector3 {
    return this.phase === 'strike' ? out.subVectors(this.lastFeet, this.releaseFeet) : out.set(0, 0, 0);
  }

  /** The farthest the aim has got from the finesse angle, in the strike (radians). */
  get feintReach(): number {
    return this.phase === 'strike' ? this.feintFar : 0;
  }

  update(dt: number, held: boolean, f: Frame): Motion | null {
    this.track(dt, f);
    this.time += dt;
    if (this.active) this.attackTime += dt;
    switch (this.phase) {
      case 'idle':
        if (held) {
          this.phase = 'windup';
          this.time = 0;
          this.hold = 0;
          this.attackTime = 0;
        }
        return null;
      case 'windup':
        this.hold += dt;
        if (!held) {
          this.phase = 'strike';
          this.time = 0;
          this.releasedAgo = 0;
          this.release(f);
        }
        return null;
      case 'strike': {
        this.releasedAgo += dt;
        this.feintStep();
        if (this.time < this.weapon.strikeDelay) return null;
        return this.land(f);
      }
      case 'recover':
        if (this.time >= this.weapon.recovery) {
          this.phase = 'idle';
          this.time = 0;
        }
        return null;
    }
  }

  private land(f: Frame): Motion {
    const arc = this.netArc();
    const m: Motion = {
      charge: this.charge,
      hold: this.hold,
      arc,
      feint: this.feintSoFar(),
      finesseDir: this.finesseAngle.clone(),
      feintFar: this.feintFar,
      circle: this.circle,
      swing: this.fastestSwing(new Vector3()),
      sweep: this.sum(this.turns, this.releasedAgo),
      path: this.strikePath(this.releasedAgo),
      travel: new Vector3().subVectors(f.feet, this.releaseFeet),
      feet: f.feet.clone(),
      eye: f.eye.clone(),
      forward: f.forward.clone(),
      up: f.up.clone(),
    };
    this.phase = 'recover';
    this.time = 0;
    this.attackTime = 0;
    return m;
  }

  /** Rotation so far: from where the aim was at the release to where it is now. */
  private netArc(): number {
    return Math.hypot(this.aimYaw - this.startYaw, PITCH_WEIGHT * (this.aimPitch - this.startPitch));
  }

  /**
   * At the release: put the finesse angle behind the swing of the moment before (see
   * RELEASE_LOOKBACK), as if it turned back the other way at FINESSE_LEAD of that swing's speed
   * for the strike delay.
   */
  private release(f: Frame): void {
    const w = this.weapon;
    this.startYaw = this.aimYaw;
    this.startPitch = this.aimPitch;
    this.releaseFeet.copy(f.feet);
    // Steps back from now: 0 is this one. The lookback runs to `first` (within the windup).
    const at = (k: number) => (this.head - 1 - k + STEPS) % STEPS;
    const rate = (k: number, axis: 0 | 1) =>
      axis === 0 ? this.rateYaw[at(k)] : PITCH_WEIGHT * this.ratePitch[at(k)];
    const window = Math.min(RELEASE_LOOKBACK, this.attackTime);
    let first = 0;
    let fastest = 0;
    let top = 0;
    for (let t = 0; first < STEPS - 2; first++) {
      const sp = Math.hypot(rate(first, 0), rate(first, 1));
      if (sp > top) {
        top = sp;
        fastest = first;
      }
      t += this.dts[at(first)];
      if (t >= window) break;
    }
    // Which way the swing was going: as the lookback began (before any switch at the release),
    // or, if the aim was barely turning then, at its fastest since.
    const early = Math.hypot(rate(first, 0), rate(first, 1));
    const from = early >= SWING_START * top ? first : fastest;
    const norm = Math.hypot(rate(from, 0), rate(from, 1));
    const uy = norm > 1e-3 ? rate(from, 0) / norm : 0;
    const up = norm > 1e-3 ? rate(from, 1) / norm : 0;
    // From where: the farthest the aim got that way (where it turned back, if it did). Its
    // speed: how fast it got there, on average (mouse input comes in uneven steps, so a single
    // step's rate can overshoot, and a short stretch of them too).
    let speed = norm;
    let anchor = from;
    if (norm > 1e-3) {
      const along = (k: number) => uy * this.yaws[at(k)] + up * PITCH_WEIGHT * this.pitches[at(k)];
      for (let k = from - 1; k >= 0; k--) if (along(k) > along(anchor)) anchor = k;
      // Back from there over up to SPEED_WINDOW s of the windup, while it was still going that way.
      let back = anchor;
      let time = 0;
      const limit = this.attackTime;
      while (back < STEPS - 2 && time < SPEED_WINDOW && time + this.dts[at(back)] <= limit) {
        if (along(back + 1) > along(back) + 1e-4) break;
        time += this.dts[at(back)];
        back++;
      }
      if (time > 1e-4) speed = (along(anchor) - along(back)) / time;
    }
    const a = at(anchor);
    const lead = FINESSE_LEAD * speed * w.strikeDelay;
    this.feintYaw = this.yaws[a] - uy * lead;
    this.feintPitch = PITCH_WEIGHT * this.pitches[a] - up * lead;
    this.feintTotal = lead;
    this.feintFar = lead;
    // The aim's way back from there to the release counts toward finesse like the strike's.
    this.lastYaw = this.yaws[a];
    this.lastWeighted = PITCH_WEIGHT * this.pitches[a];
    for (let k = anchor - 1; k >= 0; k--) {
      const i = at(k);
      this.feintPoint(this.yaws[i], PITCH_WEIGHT * this.pitches[i]);
    }
    // Its direction from the eye: the view there, turned back by that much.
    const pitch = Math.max(-1.55, Math.min(1.55, this.feintPitch / PITCH_WEIGHT));
    _a.set(this.views[a * 3], this.views[a * 3 + 1], this.views[a * 3 + 2]);
    _a.addScaledVector(f.up, -_a.dot(f.up));
    if (_a.lengthSq() < 1e-8) _a.set(1, 0, 0).addScaledVector(f.up, -f.up.x);
    _a.normalize().applyAxisAngle(f.up, -uy * lead);
    this.finesseAngle
      .copy(_a)
      .multiplyScalar(Math.cos(pitch))
      .addScaledVector(f.up, Math.sin(pitch))
      .normalize();
    this.circle = f.forward.angleTo(this.finesseAngle) >= CIRCLE_CLEAR;
  }

  /** A strike step: add the aim's turning since the last step, and how far it is from the finesse angle. */
  private feintStep(): void {
    this.feintPoint(this.aimYaw, PITCH_WEIGHT * this.aimPitch);
  }

  /** The aim reached (yaw, weighted pitch): add the turning to it, and how far it is from the finesse angle. */
  private feintPoint(yaw: number, weighted: number): void {
    this.feintTotal += Math.hypot(yaw - this.lastYaw, weighted - this.lastWeighted);
    this.feintFar = Math.max(this.feintFar, Math.hypot(yaw - this.feintYaw, weighted - this.feintPitch));
    this.lastYaw = yaw;
    this.lastWeighted = weighted;
  }

  /** Finesse so far: the farthest from the finesse angle × (1 − net / total). */
  private feintSoFar(): number {
    if (this.feintTotal < 1e-4) return 0;
    const net = Math.hypot(this.aimYaw - this.feintYaw, PITCH_WEIGHT * this.aimPitch - this.feintPitch);
    return this.feintFar * Math.max(0, 1 - net / this.feintTotal);
  }

  /** Record this step's turn about "up", the aim, the view and the feet, and the turn rate. */
  private track(dt: number, f: Frame): void {
    let turn = 0;
    const pitch = Math.asin(Math.max(-1, Math.min(1, f.forward.dot(f.up))));
    if (this.hasLast && dt > 0) {
      // The view's turn about up: both directions flattened onto the ground plane.
      const a = _a.copy(this.lastForward).addScaledVector(f.up, -this.lastForward.dot(f.up));
      const b = _b.copy(f.forward).addScaledVector(f.up, -f.forward.dot(f.up));
      if (a.lengthSq() > 1e-6 && b.lengthSq() > 1e-6) {
        turn = Math.atan2(_c.crossVectors(a, b).dot(f.up), a.dot(b));
      }
      this.aimYaw += turn;
      const k = 1 - Math.exp(-dt / AIM_SMOOTH);
      this.wYaw += (turn / dt - this.wYaw) * k;
      this.wPitch += ((pitch - this.lastPitch) / dt - this.wPitch) * k;
    }
    this.aimPitch = pitch;
    this.turns[this.head] = turn;
    this.yaws[this.head] = this.aimYaw;
    this.pitches[this.head] = pitch;
    this.views[this.head * 3] = f.forward.x;
    this.views[this.head * 3 + 1] = f.forward.y;
    this.views[this.head * 3 + 2] = f.forward.z;
    this.rateYaw[this.head] = this.wYaw;
    this.ratePitch[this.head] = this.wPitch;
    this.dts[this.head] = dt;
    this.head = (this.head + 1) % STEPS;
    this.lastForward.copy(f.forward);
    this.lastFeet.copy(f.feet);
    this.lastPitch = pitch;
    this.hasLast = true;
  }

  /** The strike's fastest swing: the tip velocity over SWING_WINDOW ending anywhere in the strike. */
  private fastestSwing(out: Vector3): Vector3 {
    const span = Math.min(SWING_SEARCH, this.releasedAgo + SWING_LEAD, this.attackTime);
    out.set(0, 0, 0);
    let best = -1;
    let t = 0;
    for (let back = 0; back < STEPS - 20 && (back === 0 || t < span); back++) {
      const window = Math.min(SWING_WINDOW, this.attackTime - t);
      this.tipVelocity(back, window, _tipV);
      const speed = _tipV.lengthSq();
      if (speed > best) {
        best = speed;
        out.copy(_tipV);
      }
      t += this.dts[(this.head - 1 - back + STEPS) % STEPS];
    }
    return out;
  }

  /**
   * The weapon tip's mean velocity from the swing over `seconds` (at least a step), ending
   * `back` steps ago.
   */
  private tipVelocity(back: number, seconds: number, out: Vector3): Vector3 {
    const end = (this.head - 1 - back + STEPS) % STEPS;
    let t = this.dts[end];
    let k = 1;
    while (k < STEPS - back - 2 && t < seconds) {
      k++;
      t += this.dts[(this.head - back - k + STEPS) % STEPS];
    }
    // The view k steps before the end, before the steps summed into t.
    const i = (this.head - back - k - 1 + STEPS) % STEPS;
    _then.set(this.views[i * 3], this.views[i * 3 + 1], this.views[i * 3 + 2]);
    out.set(this.views[end * 3], this.views[end * 3 + 1], this.views[end * 3 + 2]).sub(_then);
    return t > 0 ? out.multiplyScalar(this.weapon.reach / t) : out.set(0, 0, 0);
  }

  /** The view's directions over the last `seconds` and the step before, oldest first. */
  private strikePath(seconds: number): Vector3[] {
    let t = 0;
    let k = 1;
    while (k < STEPS - 1 && t < seconds) {
      t += this.dts[(this.head - k + STEPS) % STEPS];
      k++;
    }
    const path: Vector3[] = [];
    for (let j = k; j >= 1; j--) {
      const i = (this.head - j + STEPS) % STEPS;
      path.push(new Vector3(this.views[i * 3], this.views[i * 3 + 1], this.views[i * 3 + 2]));
    }
    return path;
  }

  /** Sum of a ring's last entries covering `seconds`. */
  private sum(ring: Float32Array, seconds: number): number {
    let s = 0;
    let t = 0;
    for (let k = 1; k <= STEPS && t < seconds; k++) {
      const i = (this.head - k + STEPS) % STEPS;
      s += ring[i];
      t += this.dts[i];
    }
    return s;
  }
}
